import { useEffect, useState } from 'react';
import { useUrlState, intParam, pageSizeParam } from '../../lib/urlState';
import { errMsg } from '../../lib/errors';
import { QueryError } from '../../lib/QueryState';
import { App, Button, Input, Select, Space, Table, Typography, type TableColumnsType } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { downloadPayoutWorkbook, type PayoutExport } from './payoutExport';
import {
  ActorCell,
  ListenerCell,
  PayToCell,
  StatusTag,
  rupees,
  when,
  type AdminPayoutRow,
} from './payoutViews';

interface HistoryPage {
  items: AdminPayoutRow[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending review' },
  { value: 'approved', label: 'Approved (not paid yet)' },
  { value: 'settled', label: 'Paid' },
  { value: 'rejected,reversed', label: 'Rejected / reversed' },
];

/**
 * Every payout request, newest first, with who approved it, who marked it paid
 * (and the payment reference) and who rejected it (and why). Server-paged,
 * filterable by status, searchable by listener name or handle.
 */
export function PayoutHistory({ listenerId }: { /** Only this listener's payouts (Listener details). */ listenerId?: string } = {}) {
  // Standalone (Payouts → History): status, search and page live in the URL so
  // Back and refresh keep them. Embedded in Listener details the same state is
  // local — that page's URL belongs to the listener, not to this table.
  const url = useUrlState();
  const embedded = Boolean(listenerId);
  const [local, setLocal] = useState({ status: '', q: '', page: 1, pageSize: 25 });
  const status = embedded ? local.status : (url.get('hstatus') ?? '');
  const q = embedded ? local.q : (url.get('q') ?? '');
  const page = embedded ? local.page : intParam(url.get('hpage'), 1);
  const pageSize = embedded ? local.pageSize : pageSizeParam(url.get('hsize'), [25, 50, 100]);
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);
  const setStatus = (v: string) =>
    embedded ? setLocal((l) => ({ ...l, status: v, page: 1 })) : url.set({ hstatus: v || null, hpage: null });
  const setQ = (v: string) =>
    embedded ? setLocal((l) => ({ ...l, q: v, page: 1 })) : url.set({ q: v || null, hpage: null });
  const setPaging = (p: number, size: number) =>
    embedded
      ? setLocal((l) => ({ ...l, page: p, pageSize: size }))
      : url.set({ hpage: p === 1 ? null : p, hsize: size === 25 ? null : size });
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);

  /** Every payout matching the current status + search — not just this page. */
  async function exportExcel() {
    setExporting(true);
    try {
      const { data } = await api.get<PayoutExport>('/admin/payouts/export', {
        params: { ...(status ? { status } : {}), ...(q ? { q } : {}), ...(listenerId ? { listenerId } : {}) },
      });
      const statusLabel = STATUS_FILTERS.find((f) => f.value === status)?.label ?? 'All statuses';
      const subtitle = [
        statusLabel,
        q ? `search “${q}”` : '',
        `exported ${new Date().toLocaleString()}`,
        data.truncated ? 'TRUNCATED — narrow the filter to get every row' : '',
      ]
        .filter(Boolean)
        .join(' · ');
      const stamp = new Date().toLocaleDateString('en-CA');
      await downloadPayoutWorkbook(data.items, subtitle, `payouts_${stamp}.xlsx`);
      if (data.truncated) message.warning('Too many payouts for one file — narrow the filter');
      else message.success(`Exported ${data.items.length} payouts`);
    } catch (e) {
      message.error(errMsg(e, 'Could not export the payouts'));
    } finally {
      setExporting(false);
    }
  }

  const history = useQuery({
    queryKey: ['admin', 'payouts', 'history', status, q, listenerId, page, pageSize],
    queryFn: async () =>
      (
        await api.get<HistoryPage>('/admin/payouts/history', {
          params: { ...(status ? { status } : {}), ...(q ? { q } : {}), ...(listenerId ? { listenerId } : {}), page, pageSize },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  const columns: TableColumnsType<AdminPayoutRow> = [
    { title: 'Listener', render: (_: unknown, row) => <ListenerCell row={row} /> },
    { title: 'Amount requested', render: (_: unknown, row) => rupees(row.amountPaise) },
    { title: 'Tax deducted (TDS)', render: (_: unknown, row) => <span style={{ color: '#FFD06A' }}>{rupees(row.tdsPaise)}</span> },
    { title: 'Amount to pay', render: (_: unknown, row) => <span style={{ fontWeight: 700 }}>{rupees(row.netPayoutPaise)}</span> },
    { title: 'Pay to', render: (_: unknown, row) => <PayToCell row={row} /> },
    { title: 'Status', render: (_: unknown, row) => <StatusTag status={row.status} /> },
    { title: 'Submitted', render: (_: unknown, row) => when(row.createdAt) },
    {
      title: 'Approved by',
      render: (_: unknown, row) => (
        <ActorCell actor={row.approvedByAdmin} at={row.approvedAt} testId={`approved-${row.id}`} />
      ),
    },
    {
      title: 'Marked paid by',
      render: (_: unknown, row) =>
        row.status === 'settled' ? (
          <ActorCell
            actor={row.paidByAdmin}
            at={row.settledAt}
            testId={`paid-${row.id}`}
            extra={
              row.paymentReference ? (
                <Typography.Text copyable style={{ fontSize: 11 }}>
                  Ref {row.paymentReference}
                </Typography.Text>
              ) : null
            }
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Rejected by',
      render: (_: unknown, row) =>
        row.status === 'rejected' || row.status === 'reversed' ? (
          <ActorCell
            actor={row.rejectedByAdmin}
            at={row.rejectedAt}
            testId={`rejected-${row.id}`}
            extra={
              row.reversalReason ? (
                <span style={{ fontSize: 11, maxWidth: 240, display: 'inline-block' }}>{row.reversalReason}</span>
              ) : null
            }
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Select
          aria-label="Status filter"
          style={{ width: 220 }}
          value={status}
          options={STATUS_FILTERS}
          onChange={setStatus}
        />
        {!listenerId && <Input.Search
          allowClear
          placeholder="Search listener name or handle"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => setQ(v.trim())}
        />}
        <Can permission="payouts.reports.export">
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportExcel()}>
            Export Excel
          </Button>
        </Can>
      </Space>
      {history.isError && !history.data && (
        <QueryError error={history.error} what="payout history" onRetry={() => void history.refetch()} />
      )}
      <Table
        rowKey="id"
        loading={history.isLoading}
        dataSource={history.data?.items ?? []}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize,
          total: history.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          onChange: (p, size) => setPaging(size !== pageSize ? 1 : p, size),
        }}
      />
    </Space>
  );
}
